"use client";

import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { emailSendVerification } from "@/server-action/email/email-send-verification";
import { useTransition, useState } from "react";
import { createAppError, AppError, AppErrorType } from "@/shared/lib/errors/error-types";
import { PasswordResetFormValues } from "@/shared/lib/validation/user-schemas";
import { checkUserEmail } from "@/server-action/user/check-user-email";
import { EmailError } from "@/shared/ui/email-error";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

interface EmailStepProps {
  onClickSendEmail: () => void;
}

export function EmailStep({ onClickSendEmail }: EmailStepProps) {
  const t = useTranslations();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<AppError | null>(null);
  const form = useFormContext<PasswordResetFormValues>();

  const handleClickSendEmail = async () => {
    const isValid = await form.trigger("email");

    if (isValid) {
      startTransition(async () => {
        try {
          const checkUserEmailResult = await checkUserEmail(form.getValues("email"));
          if (!checkUserEmailResult.ok) {
            throw new Error(checkUserEmailResult.error.message);
          }
          const isUserExists = checkUserEmailResult.data;

          if (!isUserExists) {
            setError(createAppError("User not found"));
            return;
          }

          const result = await emailSendVerification(form.getValues("email"), "reset-password");
          if (!result.ok) {
            throw new Error(result.error.message);
          }
          onClickSendEmail();
        } catch (error: unknown) {
          if (error instanceof Error) {
            const appError = createAppError(error.message);
            setError(appError);
          } else {
            setError(createAppError("Unknown error"));
          }
        }
      });
    }
  };

  return (
    <div className="pc:max-w-[420px] w-full">
      {error && error.type === AppErrorType.USER_NOT_FOUND ? (
        <EmailError
          title={t("f_ua_title")}
          description={t("f_ua_context")}
          button1={{
            label: t("f_ua_btn_re"),
            onClick: () => {
              form.resetField("email");
              setError(null);
            },
          }}
          button2={{ label: t("f_ua_btn_join"), onClick: () => router.push("/signup") }}
        />
      ) : (
        <>
          <h3 className="heading-3-bold tb:mb-10 mb-8 text-center text-gray-900">{t("f_pf_title")}</h3>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="email" className="body-md-bold text-gray-900">
                  {t("f_pf_lable_id")}
                </FormLabel>
                <FormControl>
                  <Input
                    id="email"
                    type="email"
                    variant="public"
                    placeholder={t("f_pf_placeholder_id")}
                    {...field}
                    maxLength={50}
                  />
                </FormControl>
                <FormMessage className="body-sm-regular text-red-500" />
              </FormItem>
            )}
          />

          <Button
            type="button"
            className="mt-10 w-full"
            disabled={isPending}
            variant="primary"
            size="lg"
            onClick={handleClickSendEmail}
          >
            {t("f_pf_btn")}
          </Button>
        </>
      )}
    </div>
  );
}
